import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  IconButton,
  Switch,
  FormControlLabel,
  useTheme,
  Grid,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { formatPhoneNumber } from "../../Core/util/formatPhone";

interface Transaction {
  _id: string;
  email: string;
  licenseNumber: string;
  userId?: {
    _id: string;
    phoneNumber?: string;
    countryCode?: string;
    name?: string;
  };
  breakdown: {
    grandTotal: number;
    currency: string;
  };
  items: Array<{
    name: string;
    type: string;
    unitPrice: number;
    quantity: number;
  }>;
  paymentInfo: {
    razorpay_payment_id: string;
  };
  dates: {
    createdAt: string;
    newExpiry: string;
  };
  status: string;
  gstDetails?: {
    gstin?: string;
    businessName?: string;
    address?: string;
  };
}

export default function Subscriptions() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'date', direction: 'desc' });
  
  const [licenseData, setLicenseData] = useState<any>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Expiry / status forms to avoid window hacks
  const [localUpdates, setLocalUpdates] = useState<any>({
    accosync: { expiry: "", status: "" },
    analytics: { expiry: "", status: "" },
    mart: { expiry: "", status: "" },
  });

  const fetchTransactions = async (currentPage = page, search = searchTerm, sort = sortConfig) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ipconfig}/admin/subscriptions/transactions`, {
        params: {
          page: currentPage,
          limit: 50,
          search: search,
          sortBy: sort.key,
          sortDir: sort.direction
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status) {
        setTransactions(response.data.data || []);
        setTotalPages(response.data.pagination.totalPages);
        setTotalItems(response.data.pagination.total);
      }
    } catch (error: any) {
      console.error("Error fetching transactions:", error);
      toast.error(`Error fetching transactions: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const checkLicenseStatus = async (licenseNum: string) => {
    setStatusLoading(true);
    setIsModalOpen(true);
    // Reset inputs
    setLocalUpdates({
      accosync: { expiry: "", status: "" },
      analytics: { expiry: "", status: "" },
      mart: { expiry: "", status: "" },
    });

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ipconfig}/admin/subscriptions/status?licenseNumber=${licenseNum}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status) {
        const licData = response.data.data;
        setLicenseData(licData);
        
        // Initialize values
        const initialUpdates: any = {};
        ["accosync", "analytics", "mart"].forEach(svc => {
          let service = licData[`${svc}optedservice`] || (licData.services && licData.services[svc]);
          if (svc === 'accosync' && !service && licData.services) {
            if (licData.services.status !== undefined) service = licData.services;
          }
          if (service) {
            initialUpdates[svc] = {
              status: service.status || "expired",
              expiry: "",
            };
          } else {
            initialUpdates[svc] = { expiry: "", status: "" };
          }
        });
        setLocalUpdates(initialUpdates);
      } else {
        toast.error("License not found or error fetching status.");
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error checking license status:", error);
      toast.error("Error checking license status.");
      setIsModalOpen(false);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleUpdateExpiry = async (licenseNumber: string, serviceType: string) => {
    try {
      const stateVal = localUpdates[serviceType];
      const newExpiryRaw = stateVal.expiry;
      const manualStatus = stateVal.status;
      
      const formattedDate = newExpiryRaw ? newExpiryRaw.split('-').reverse().join('-') : undefined;
      
      if (!formattedDate && !manualStatus) {
        toast.info("Please select a new date or change status.");
        return;
      }

      const token = localStorage.getItem("token");
      const response = await axios.put(`${ipconfig}/admin/subscriptions/update-expiry`, {
        licenseNumber,
        serviceType,
        newExpiryDate: formattedDate,
        status: manualStatus
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status) {
        toast.success("Subscription updated successfully!");
        checkLicenseStatus(licenseNumber); // Refresh status
      }
    } catch (error) {
      console.error("Error updating expiry:", error);
      toast.error("Error updating expiry.");
    }
  };

  useEffect(() => {
    fetchTransactions(page, searchTerm, sortConfig);
  }, [page, sortConfig]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchTransactions(1, searchTerm, sortConfig);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const SortHeader = ({ label, sortKey }: { label: string, sortKey: string }) => (
    <TableCell 
      onClick={() => handleSort(sortKey)} 
      sx={{
        cursor: 'pointer',
        fontWeight: 700,
        backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b",
        '&:hover': { backgroundColor: theme.palette.mode === "light" ? "#f1f5f9" : "#334155" },
        userSelect: "none"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        {sortConfig?.key === sortKey ? (
          sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
        ) : (
          <ArrowUpward fontSize="small" sx={{ opacity: 0.2 }} />
        )}
      </Box>
    </TableCell>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", p: 2, boxSizing: "border-box", gap: "20px" }}>
      {/* Top Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
          Subscription Transactions
        </Typography>
        <TextField
          variant="outlined"
          placeholder="Search by Email, License, Payment ID..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: { xs: '100%', sm: 380 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Main Table Panel */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          flexGrow: 1,
          borderRadius: "12px",
          borderColor: "divider",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "5px",
            height: "5px"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "divider",
            borderRadius: "4px"
          }
        }}
      >
        <Table stickyHeader size="medium">
          <TableHead>
            <TableRow>
              <SortHeader label="Date" sortKey="date" />
              <SortHeader label="Email" sortKey="email" />
              <SortHeader label="User Details" sortKey="name" />
              <SortHeader label="License Number" sortKey="licenseNumber" />
              <SortHeader label="Items" sortKey="items" />
              <SortHeader label="Amount" sortKey="amount" />
              <SortHeader label="Payment ID" sortKey="paymentId" />
              <SortHeader label="Expiry Date" sortKey="newExpiry" />
              <SortHeader label="Status" sortKey="status" />
              <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b", position: 'sticky', top: 0, zIndex: 2 }}>GST Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No transactions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((transaction: Transaction) => (
                <TableRow
                  key={transaction._id}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b50",
                    },
                    transition: "all 0.15s ease",
                  }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {new Date(transaction.dates.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      onClick={() => {
                        const uid = typeof transaction.userId === "object" ? transaction.userId?._id : transaction.userId;
                        navigate("/user", { state: { userId: uid, email: transaction.email, search: transaction.email, tab: 2 } });
                      }}
                      sx={{ 
                        color: 'primary.main', 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { textDecoration: "underline" }
                      }}
                    >
                      {transaction.email}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      const uid = typeof transaction.userId === "object" ? transaction.userId?._id : transaction.userId;
                      navigate("/user", { state: { userId: uid, email: transaction.email, search: transaction.email, tab: 2 } });
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        color: 'primary.main',
                        '&:hover': { textDecoration: "underline" }
                      }}
                    >
                      {transaction.userId?.name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                      {formatPhoneNumber(transaction.userId?.phoneNumber, transaction.userId?.countryCode) !== "N/A" ? formatPhoneNumber(transaction.userId?.phoneNumber, transaction.userId?.countryCode) : 'No Phone'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2"
                      onClick={() => checkLicenseStatus(transaction.licenseNumber)}
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main', 
                        fontWeight: 700,
                        '&:hover': { textDecoration: "underline" }
                      }}
                    >
                      {transaction.licenseNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {transaction.items.map((item: any, idx: number) => (
                      <Chip
                        key={idx}
                        label={`${item.name} ${item.quantity > 1 ? `x${item.quantity}` : ""}`}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 0.5, mb: 0.5, fontSize: '11px', fontWeight: 555 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>
                    {transaction.breakdown.currency === "INR" || transaction.breakdown.currency === "₹" ? "₹" : transaction.breakdown.currency} {transaction.breakdown.grandTotal}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', color: "text.secondary" }}>
                    {transaction.paymentInfo.razorpay_payment_id}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {transaction.dates.newExpiry}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.status}
                      color={transaction.status === "captured" ? "success" : "warning"}
                      size="small"
                      sx={{ fontSize: '11px', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    {transaction.gstDetails?.gstin ? (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: "8px" }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '12px', color: "text.primary" }}>
                            {transaction.gstDetails.gstin}
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block", color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {transaction.gstDetails.businessName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px', display: 'block', maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {transaction.gstDetails.address}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          sx={{ p: 0.5 }}
                          onClick={() => {
                            const text = `GSTIN: ${transaction.gstDetails?.gstin}\nName: ${transaction.gstDetails?.businessName}\nAddress: ${transaction.gstDetails?.address}`;
                            navigator.clipboard.writeText(text);
                            toast.success("GST details copied!");
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: '14px' }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">N/A</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Total {totalItems} transactions
        </Typography>
        <Box sx={{ display: 'flex', gap: "12px", alignItems: 'center' }}>
          <Button 
            size="small" 
            variant="outlined" 
            disabled={page === 1 || loading} 
            onClick={() => setPage(p => p - 1)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Page {page} of {totalPages}
          </Typography>
          <Button 
            size="small" 
            variant="outlined" 
            disabled={page === totalPages || loading} 
            onClick={() => setPage(p => p + 1)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* License Status & Edit Dialog */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" }
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700 }}>
          License Subscription Management
          <IconButton onClick={() => setIsModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ p: "24px" }}>
          {statusLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : licenseData ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Tally License: {licenseData.tallyLicenseNumber}
              </Typography>
              <Grid container spacing={3}>
                {["accosync", "analytics", "mart"].map((svc) => {
                  let service = licenseData[`${svc}optedservice`];
                  if (!service && licenseData.services) {
                    if (licenseData.services[svc]) {
                      service = licenseData.services[svc];
                    } else if (svc === 'accosync') {
                      service = licenseData.services;
                    }
                  }

                  const isOpted = service && (service.expiryDate || service.status);
                  const isSvcActive = (localUpdates[svc]?.status || service?.status) === "active";

                  return (
                    <Grid item xs={12} md={4} key={svc}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: "16px",
                          borderRadius: "12px",
                          height: "100%",
                          backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b30",
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px"
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography sx={{ textTransform: "capitalize", fontWeight: 750, fontSize: "14px" }}>
                            {svc} Service
                          </Typography>
                          {isOpted && (
                            <FormControlLabel
                              control={
                                <Switch 
                                  size="small"
                                  checked={isSvcActive}
                                  onChange={(e) => {
                                    const newStatus = e.target.checked ? 'active' : 'expired';
                                    setLocalUpdates((prev: any) => ({
                                      ...prev,
                                      [svc]: { ...prev[svc], status: newStatus }
                                    }));
                                  }}
                                />
                              }
                              label={<Typography variant="caption" sx={{ fontWeight: 700 }}>Active</Typography>}
                              labelPlacement="start"
                              sx={{ m: 0, gap: "4px" }}
                            />
                          )}
                        </Box>
                        <Divider />
                        {isOpted ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", flexGrow: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>Expiry:</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>{service.expiryDate || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>Status:</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>{service.status || 'N/A'}</Typography>
                            </Box>

                            <TextField
                              label="New Expiry Date"
                              type="date"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              value={localUpdates[svc].expiry}
                              onChange={(e) => {
                                setLocalUpdates((prev: any) => ({
                                  ...prev,
                                  [svc]: { ...prev[svc], expiry: e.target.value }
                                }));
                              }}
                            />
                            <Button 
                              variant="contained"
                              size="small"
                              onClick={() => handleUpdateExpiry(licenseData.tallyLicenseNumber, svc)}
                              sx={{ textTransform: 'none', mt: 1, fontWeight: 600 }} 
                            >
                              Update Service
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center", py: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                              Not Opted
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : (
            <Typography>No data available</Typography>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: "16px 24px" }}>
          <Button onClick={() => setIsModalOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer style={{ width: "auto" }} />
    </Box>
  );
}
